import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import { toast } from 'sonner';
import {
  Save,
  BookOpen,
  Loader2,
  CheckCircle2,
  Circle,
  Copy,
  ExternalLink,
} from 'lucide-react';

const PROVIDERS = [
  {
    id: 'google',
    label: 'Google',
    color: '#4285F4',
    console_url: 'https://console.cloud.google.com/apis/credentials',
    scope_default: 'openid email profile',
    supports_tenant: false,
  },
  {
    id: 'microsoft',
    label: 'Microsoft',
    color: '#0078D4',
    console_url: 'https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade',
    scope_default: 'openid profile email offline_access',
    supports_tenant: true,
  },
];

// tiny markdown renderer — keeps deps zero. Supports #/##/### headings, bold, code, lists, links.
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split('\n');
  const out = [];
  let inList = false;
  const closeList = () => {
    if (inList) {
      out.push(`</ul>`);
      inList = false;
    }
  };
  const inline = (s) =>
    s
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>')
      .replace(/`([^`]+)`/g, '<code class="bg-slate-100 px-1 py-0.5 text-[11px]">$1</code>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-primary underline">$1</a>')
      .replace(/<((?:https?:\/\/|mailto:)[^>]+)>/g, '<a href="$1" target="_blank" rel="noreferrer" class="text-primary underline">$1</a>');
  for (const line of lines) {
    if (/^### /.test(line)) { closeList(); out.push(`<h4 class="text-sm font-bold mt-4 mb-1">${inline(line.slice(4))}</h4>`); continue; }
    if (/^## /.test(line))  { closeList(); out.push(`<h3 class="text-base font-black mt-6 mb-2">${inline(line.slice(3))}</h3>`); continue; }
    if (/^# /.test(line))   { closeList(); out.push(`<h2 class="text-lg font-black mt-6 mb-2">${inline(line.slice(2))}</h2>`); continue; }
    if (/^- /.test(line))   {
      if (!inList) { out.push(`<ul class="list-disc ml-5 space-y-1 my-2 text-sm">`); inList = true; }
      out.push(`<li>${inline(line.slice(2))}</li>`);
      continue;
    }
    if (line.trim() === '') { closeList(); out.push(`<div class="h-2"></div>`); continue; }
    closeList();
    out.push(`<p class="text-sm text-slate-700 leading-relaxed my-2">${inline(line)}</p>`);
  }
  closeList();
  return { __html: out.join('') };
}

/**
 * SuperAdmin > Social Sign-in tab body.
 * Rendered inline inside SuperAdmin.js via {activeTab === 'oauth'}.
 * No page wrapper / top-nav — the SuperAdmin shell provides those.
 */
export default function OAuthConfigTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [configs, setConfigs] = useState({});
  const [drafts, setDrafts] = useState({});
  const [docsDrawer, setDocsDrawer] = useState({ open: false, provider: null, markdown: '', redirect_uri: '' });
  const [docsLoading, setDocsLoading] = useState(false);

  useEffect(() => {
    reload();
  }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/super-admin/oauth/configs');
      setConfigs(data?.providers || {});
      const nextDrafts = {};
      for (const p of PROVIDERS) {
        const cfg = data?.providers?.[p.id] || {};
        nextDrafts[p.id] = {
          enabled: cfg.enabled || false,
          client_id: cfg.client_id || '',
          client_secret: '',
          redirect_uri: cfg.redirect_uri || cfg.redirect_uri_suggested || '',
          scope: cfg.scope || cfg.scope_suggested || p.scope_default,
          tenant_id: cfg.tenant_id || (p.id === 'microsoft' ? 'common' : ''),
        };
      }
      setDrafts(nextDrafts);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load OAuth configs');
    } finally {
      setLoading(false);
    }
  };

  const setDraft = (provider, field, value) => {
    setDrafts((d) => ({ ...d, [provider]: { ...d[provider], [field]: value } }));
  };

  const save = async (provider) => {
    setSaving(provider);
    try {
      const draft = drafts[provider] || {};
      const payload = {
        enabled: !!draft.enabled,
        client_id: (draft.client_id || '').trim(),
        redirect_uri: (draft.redirect_uri || '').trim(),
        scope: (draft.scope || '').trim(),
        tenant_id: (draft.tenant_id || '').trim(),
      };
      if (draft.client_secret && draft.client_secret.trim().length > 0) {
        payload.client_secret = draft.client_secret.trim();
      }
      await api.put(`/super-admin/oauth/configs/${provider}`, payload);
      toast.success(`${provider} config saved`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  const openDocs = async (provider) => {
    setDocsDrawer({ open: true, provider, markdown: '', redirect_uri: '' });
    setDocsLoading(true);
    try {
      const { data } = await api.get(`/super-admin/oauth/docs/${provider}`);
      setDocsDrawer({ open: true, provider, markdown: data?.markdown || '', redirect_uri: data?.redirect_uri || '' });
    } catch (e) {
      toast.error('Could not load setup docs');
      setDocsDrawer({ open: false, provider: null, markdown: '', redirect_uri: '' });
    } finally {
      setDocsLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error('Copy failed')
    );
  };

  return (
    <div className="max-w-5xl" data-testid="oauth-config-tab">
      <div className="mb-6">
        <h2 className="text-xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Social sign-in configuration
        </h2>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Enable "Continue with Google" and "Continue with Microsoft" on the signup + login pages.
          Follow the setup docs for each provider to obtain the client credentials, then paste them below.
        </p>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2"/>Loading…
        </div>
      )}

      {!loading && PROVIDERS.map((p) => {
        const cfg = configs[p.id] || {};
        const draft = drafts[p.id] || {};
        const configured = cfg.has_secret && cfg.client_id;
        return (
          <div
            key={p.id}
            className="bg-white border border-slate-200 mb-6 overflow-hidden"
            data-testid={`oauth-provider-${p.id}`}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-3">
                <div
                  className="h-9 w-9 flex items-center justify-center text-white font-black text-lg"
                  style={{ backgroundColor: p.color }}
                >
                  {p.label[0]}
                </div>
                <div>
                  <div className="text-base font-black tracking-tight flex items-center gap-2">
                    {p.label}
                    {configured ? (
                      <Badge className="rounded-none bg-primary/10 text-primary hover:bg-primary/10">
                        <CheckCircle2 className="h-3 w-3 mr-1"/> Configured
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="rounded-none text-slate-500">
                        <Circle className="h-3 w-3 mr-1"/> Not configured
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    OAuth 2.0 · authorization code flow
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-none"
                  onClick={() => openDocs(p.id)}
                  data-testid={`oauth-docs-btn-${p.id}`}
                >
                  <BookOpen className="h-4 w-4 mr-2"/>
                  Setup guide
                </Button>
                <a
                  href={p.console_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  Open {p.label} console <ExternalLink className="h-3 w-3"/>
                </a>
              </div>
            </div>

            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="lg:col-span-2 flex items-center justify-between p-3 bg-slate-50">
                <div>
                  <div className="text-sm font-semibold">
                    Enable {p.label} sign-in on signup + login pages
                  </div>
                  <div className="text-xs text-slate-500">
                    Users won't see the "Continue with {p.label}" button until enabled.
                  </div>
                </div>
                <Switch
                  checked={!!draft.enabled}
                  onCheckedChange={(v) => setDraft(p.id, 'enabled', v)}
                  data-testid={`oauth-enabled-${p.id}`}
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wide font-medium">Client ID</Label>
                <Input
                  className="rounded-none mt-1 font-mono text-xs"
                  placeholder="from provider's console"
                  value={draft.client_id || ''}
                  onChange={(e) => setDraft(p.id, 'client_id', e.target.value)}
                  data-testid={`oauth-client-id-${p.id}`}
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wide font-medium">
                  Client Secret {cfg.has_secret && <span className="text-primary normal-case">· stored ✓</span>}
                </Label>
                <Input
                  className="rounded-none mt-1 font-mono text-xs"
                  type="password"
                  placeholder={cfg.has_secret ? '••••••• (leave blank to keep current)' : 'from provider\'s console'}
                  value={draft.client_secret || ''}
                  onChange={(e) => setDraft(p.id, 'client_secret', e.target.value)}
                  data-testid={`oauth-client-secret-${p.id}`}
                />
              </div>

              <div className="lg:col-span-2">
                <Label className="text-xs uppercase tracking-wide font-medium flex items-center justify-between">
                  <span>Redirect URI (paste this into {p.label}'s console)</span>
                  <button
                    className="text-primary hover:underline text-[11px] flex items-center gap-1 normal-case tracking-normal"
                    onClick={() => copyToClipboard(draft.redirect_uri || cfg.redirect_uri_suggested, 'Redirect URI')}
                    data-testid={`oauth-copy-redirect-${p.id}`}
                  >
                    <Copy className="h-3 w-3"/> Copy
                  </button>
                </Label>
                <Input
                  className="rounded-none mt-1 font-mono text-xs"
                  value={draft.redirect_uri || cfg.redirect_uri_suggested || ''}
                  onChange={(e) => setDraft(p.id, 'redirect_uri', e.target.value)}
                  data-testid={`oauth-redirect-uri-${p.id}`}
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wide font-medium">Scope</Label>
                <Input
                  className="rounded-none mt-1 font-mono text-xs"
                  value={draft.scope || ''}
                  onChange={(e) => setDraft(p.id, 'scope', e.target.value)}
                  data-testid={`oauth-scope-${p.id}`}
                />
              </div>

              {p.supports_tenant && (
                <div>
                  <Label className="text-xs uppercase tracking-wide font-medium">Tenant ID</Label>
                  <Input
                    className="rounded-none mt-1 font-mono text-xs"
                    placeholder="common · organizations · or your tenant GUID"
                    value={draft.tenant_id || ''}
                    onChange={(e) => setDraft(p.id, 'tenant_id', e.target.value)}
                    data-testid={`oauth-tenant-id-${p.id}`}
                  />
                </div>
              )}

              <div className="lg:col-span-2 flex items-center justify-end gap-2 pt-2">
                <Button
                  onClick={() => save(p.id)}
                  disabled={saving === p.id}
                  className="rounded-none"
                  data-testid={`oauth-save-btn-${p.id}`}
                >
                  {saving === p.id ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin"/>Saving…</>
                  ) : (
                    <><Save className="h-4 w-4 mr-2"/>Save {p.label} settings</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        );
      })}

      {/* Docs drawer */}
      <Sheet open={docsDrawer.open} onOpenChange={(o) => setDocsDrawer((s) => ({ ...s, open: o }))}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl overflow-y-auto"
          data-testid={`oauth-docs-drawer${docsDrawer.provider ? `-${docsDrawer.provider}` : ''}`}
        >
          <SheetHeader>
            <SheetTitle className="text-2xl font-black tracking-tight capitalize">
              {docsDrawer.provider} · Setup guide
            </SheetTitle>
            <SheetDescription>
              Step-by-step to obtain your OAuth credentials.
            </SheetDescription>
          </SheetHeader>
          {docsDrawer.redirect_uri && (
            <div className="mt-4 mb-6 bg-primary/5 border border-primary/20 p-3">
              <div className="text-[10px] uppercase tracking-widest text-primary font-semibold mb-1">
                Paste this in {docsDrawer.provider}'s "Authorized redirect URI" field
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs font-mono bg-white px-2 py-1 border border-primary/20">
                  {docsDrawer.redirect_uri}
                </code>
                <button
                  onClick={() => copyToClipboard(docsDrawer.redirect_uri, 'Redirect URI')}
                  className="text-primary hover:underline text-xs flex items-center gap-1"
                >
                  <Copy className="h-3 w-3"/> Copy
                </button>
              </div>
            </div>
          )}
          {docsLoading ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin"/>
            </div>
          ) : (
            <div className="prose prose-sm max-w-none mt-6"
                 dangerouslySetInnerHTML={renderMarkdown(docsDrawer.markdown)}/>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
